/**
 * @copyright (C) Microsoft Corporation.
 * All Rights Reserved.
 */
import React from "react";
import { MessageBar, MessageBarButton, MessageBarType, ProgressIndicator } from "@fluentui/react";
import {
    EntraLinkingStatus,
    IEntraLinkingInfoDTO,
    IEntraLinkingStatusDTO,
    useHapAuth
} from "@nuance/hap-components";
import { useTranslation } from "react-i18next";
import { getLogger } from "@nuance/hux-diagnostics";
import { EventTypes } from "../EventTypes";

const logger = getLogger();

/**
 * Props for the EntraLinkingProgressAndMessage
 */
export interface IEntraLinkingProgressAndMessageProps {
    entraLinkingInfo: IEntraLinkingStatusDTO | IEntraLinkingInfoDTO;
    messageBarButtonAction?: () => void;
}

/**
 * Renders the entra linking message and progress bar
 * @param status The status of the linking
 * @returns A fragment or MessageBar
 */
export const EntraLinkingProgressAndMessage = (props: IEntraLinkingProgressAndMessageProps) => {
    const { t } = useTranslation();
    const { userContext } = useHapAuth();
    let messageBarType: MessageBarType = MessageBarType.success;
    let messageBarMessage: string = t("EntraUserLink.Linking_Status.Complete");
    let messageBarButton: JSX.Element | undefined = undefined;

    const getCompletedAtDate = (formatString: string) => {
        const dateOptions: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        };
        const timeOptions: Intl.DateTimeFormatOptions = {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZoneName: "short"
        };
        return t(formatString, {
            Date: Intl.DateTimeFormat(navigator.language, dateOptions).format(
                new Date(props.entraLinkingInfo.completedAt)
            ),
            Time: Intl.DateTimeFormat(navigator.language, timeOptions).format(
                new Date(props.entraLinkingInfo.completedAt)
            )
        });
    };
    let progressLabel: string = getCompletedAtDate(t("EntraUserLink.Linking_Completed_Text"));
    if (
        props.messageBarButtonAction &&
        (props.entraLinkingInfo.status == EntraLinkingStatus.Succeeded ||
            props.entraLinkingInfo.status == EntraLinkingStatus.Canceled)
    ) {
        const action = props.messageBarButtonAction;
        messageBarButton = (
            <MessageBarButton
                text={t("EntraUserLink.Actions.View_Details")}
                onClick={() => {
                    logger.trackEvent(EventTypes.ViewEntraMappingDetailsClicked, {
                        entraLinkingTaskGuid: props.entraLinkingInfo.entraLinkingTaskGuid,
                        userGuid: userContext.user.guid
                    });
                    action();
                }}
            />
        );
    }
    const userMapped =
        (props.entraLinkingInfo as IEntraLinkingStatusDTO).mappings?.filter(
            mapping => mapping.isCompleted && !mapping.hasError
        )?.length ?? 0;
    const usersWithError =
        (props.entraLinkingInfo as IEntraLinkingStatusDTO).mappings?.filter(
            mapping => mapping.isCompleted && mapping.hasError
        )?.length ?? 0;
    let progressDescription = t("EntraUserLink.Linking_Users.Complete", {
        0: userMapped
    });
    if (props.entraLinkingInfo.status === EntraLinkingStatus.Running) {
        return (
            <ProgressIndicator
                label={t("EntraUserLink.Linking_Status.In_Progress")}
                description={t("EntraUserLink.Linking_Users.InProgress", {
                    0: userMapped + usersWithError,
                    1: props.entraLinkingInfo.totalUsers
                })}
                percentComplete={
                    props.entraLinkingInfo.lastRowMapped / props.entraLinkingInfo.totalUsers
                }
            />
        );
    } else if (
        props.entraLinkingInfo.status === EntraLinkingStatus.Succeeded &&
        usersWithError > 0
    ) {
        messageBarType = MessageBarType.warning;
        messageBarMessage = t("EntraUserLink.Linking_Status.Complete_With_Errors");
        progressDescription = t("EntraUserLink.Linking_Users.Partial", {
            0: userMapped,
            1: props.entraLinkingInfo.totalUsers
        });
    } else if (props.entraLinkingInfo.status === EntraLinkingStatus.Canceled) {
        messageBarType = MessageBarType.info;
        messageBarMessage = t("EntraUserLink.Linking_Status.Cancelled");
        progressLabel = getCompletedAtDate(t("EntraUserLink.Linking_Cancelled_Text"));
        progressDescription = t("EntraUserLink.Linking_Users.Partial", {
            0: userMapped,
            1: props.entraLinkingInfo.totalUsers
        });
    } else if (props.entraLinkingInfo.status === EntraLinkingStatus.Failed) {
        messageBarType = MessageBarType.error;
        messageBarMessage = t("EntraUserLink.Linking_Status.Failed");
        progressLabel = getCompletedAtDate(t("EntraUserLink.Linking_Stopped_Text"));
        progressDescription = t("EntraUserLink.Linking_Users.Partial", {
            0: userMapped,
            1: props.entraLinkingInfo.totalUsers
        });
    } else if (props.entraLinkingInfo.status === EntraLinkingStatus.NotStarted) {
        return (
            <MessageBar messageBarType={MessageBarType.info} isMultiline={false} aria-live="off">
                {t("EntraUserLink.Linking_Status.Not_Started")}
            </MessageBar>
        );
    }

    return (
        <>
            <MessageBar
                styles={{ root: { padding: "3px" } }}
                messageBarType={messageBarType}
                isMultiline={false}
                aria-live="off"
                actions={messageBarButton}
            >
                {messageBarMessage}
            </MessageBar>
            <div
                style={{
                    width: "100%",
                    paddingBottom: "12px",
                    paddingTop: props.messageBarButtonAction ? "0px" : "12px"
                }}
            >
                <ProgressIndicator
                    label={progressLabel}
                    description={progressDescription}
                    percentComplete={
                        props.entraLinkingInfo.lastRowMapped / props.entraLinkingInfo.totalUsers
                    }
                />
            </div>
        </>
    );
};
